import type { ActivityEvent } from "../api/types";

/**
 * Pure activity-feed helpers (unit-tested in activity.test.ts): the verb →
 * {kind,label} mapping that drives the row icon/tint, plus the article-unit
 * deep-link helpers. No React here — the icon component nodes are chosen from
 * `kind` in the screen.
 *
 * ⚠️ Relative-time phrasing is NOT here. This file used to export a
 * `relativeTime` — so did `notifications/relTime.ts`, with different thresholds
 * and different arithmetic, under the same name. The single implementation now
 * lives in `lib/format.ts` alongside `formatDate`/`formatDateTime`, because it
 * is shared date formatting and because all of it swaps to
 * `@norkron/web-datetime` as one unit. Do not re-add a local copy: import
 * `relativeTime` from `./format`.
 */

/** The visual/semantic family of an event verb. `event` is the catch-all so an
 *  unknown verb still renders (never a blank row). */
export type ActivityKind = "delivered" | "invited" | "joined" | "event";

export interface ActivityMeta {
  kind: ActivityKind;
  /** A short, accessible label for the verb (screen-reader + tint pill). */
  label: string;
}

const VERB_META: Record<string, ActivityMeta> = {
  delivered: { kind: "delivered", label: "Delivered" },
  invited: { kind: "invited", label: "Invited" },
  joined: { kind: "joined", label: "Joined" },
};

export function activityMeta(verb: string): ActivityMeta {
  return VERB_META[verb] ?? { kind: "event", label: titleCase(verb) };
}

function titleCase(s: string): string {
  if (!s) return "Activity";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The display name for an event's actor — "System" when the event has no live
 *  actor (a server/seed-recorded event). */
export function actorLabel(ev: Pick<ActivityEvent, "actor_name">): string {
  return ev.actor_name ?? "System";
}

// ── IA article-first sweep helpers (pure — unit-tested in activity.test.ts) ──

/** The flat ArticleCtx subset an activity row carries (mirrors models.ArticleCtx
 *  on the server). Kept a local shape so the screen imports one narrow type. */
export type ActivityArticleCtx = Pick<
  ActivityEvent,
  "article_unit_id" | "resource_role" | "unit_title" | "unit_slug"
>;

/** Does this event belong to an article-unit? True only when the backend
 *  resolved a unit id (a document event whose doc is unit-attached). Invite/join/
 *  system + loose-doc events are false → they render as the classic flat row. */
export function hasArticleContext(
  ev: Pick<ActivityEvent, "article_unit_id">,
): boolean {
  return ev.article_unit_id != null;
}

/** The workbench deep link for a unit-attached event:
 *  `/w/<slug>/article/<unitId>?show=<role>` — dropping into the exact pane the
 *  event touched. Returns null for a loose/system event (no link — the row stays
 *  a plain `<li>`). `slug` is the WORKSPACE slug (route base); the unit id is the
 *  numeric `article_unit_id`. */
export function activityUnitHref(
  slug: string,
  ev: ActivityArticleCtx,
): string | null {
  if (ev.article_unit_id == null) return null;
  const role = ev.resource_role;
  const q = role && role.length > 0 ? `?show=${encodeURIComponent(role)}` : "";
  return `/w/${encodeURIComponent(slug)}/article/${ev.article_unit_id}${q}`;
}

/** Title-case a resource role for the row's context chip ("brief" → "Brief").
 *  Empty / missing → "" (the chip is then not rendered). */
export function roleChipLabel(role: string | undefined): string {
  if (!role) return "";
  return role.charAt(0).toUpperCase() + role.slice(1);
}
